import { useCallback, useEffect, useState } from 'react'
import {
  Trash2,
  RotateCcw,
  FolderOpen,
  FileText,
  AlertTriangle,
  X,
} from 'lucide-react'
import { TopBar } from '../components/TopBar'
import { useI18n } from '../lib/i18n'

// ─── i18n ────────────────────────────────────────────────────────────────────
const LABELS = {
  zh: {
    pageTitle: '回收站',
    pageDesc: '已删除的案件和文件将在此暂存，可随时恢复。',
    empty: '回收站为空',
    emptyDesc: '删除的内容会暂存在这里，方便您恢复。',
    restore: '恢复',
    deletePermanent: '永久删除',
    matter: '案件',
    file: '文件',
    deletedAt: '删除时间',
    confirmTitle: '确认永久删除',
    confirmDesc: '此操作不可撤销，确定要永久删除吗？',
    cancel: '取消',
    confirm: '确定删除',
    restoreSuccess: '恢复成功',
    deleteSuccess: '已永久删除',
    belongsTo: '所属案件',
  },
  en: {
    pageTitle: 'Recycle Bin',
    pageDesc: 'Deleted cases and files are stored here. You can restore them at any time.',
    empty: 'Recycle bin is empty',
    emptyDesc: 'Deleted items will be temporarily stored here for easy recovery.',
    restore: 'Restore',
    deletePermanent: 'Delete Permanently',
    matter: 'Case',
    file: 'File',
    deletedAt: 'Deleted at',
    confirmTitle: 'Confirm Permanent Delete',
    confirmDesc: 'This action cannot be undone. Are you sure?',
    cancel: 'Cancel',
    confirm: 'Delete',
    restoreSuccess: 'Restored successfully',
    deleteSuccess: 'Permanently deleted',
    belongsTo: 'Belongs to',
  },
} as const

interface TrashItem {
  item_type: 'matter' | 'file'
  item_id: string
  title: string
  subtitle: string | null
  deleted_at: string
}

export function RecycleBin() {
  const { lang } = useI18n()
  const t = LABELS[lang] ?? LABELS.zh
  const [items, setItems] = useState<TrashItem[]>([])
  const [loading, setLoading] = useState(true)
  const [confirmDelete, setConfirmDelete] = useState<TrashItem | null>(null)

  const loadItems = useCallback(() => {
    setLoading(true)
    fetch('/matters/trash/list')
      .then((r) => r.json())
      .then((data: { items?: TrashItem[] }) => {
        if (data.items) setItems(data.items)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  const handleRestore = async (item: TrashItem) => {
    try {
      const res = await fetch(`/matters/trash/${item.item_type}/${item.item_id}/restore`, {
        method: 'POST',
      })
      if (res.ok) loadItems()
    } catch { /* ignore */ }
  }

  const handlePermanentDelete = async (item: TrashItem) => {
    try {
      const res = await fetch(`/matters/trash/${item.item_type}/${item.item_id}/permanent`, {
        method: 'DELETE',
      })
      if (res.ok) {
        setConfirmDelete(null)
        loadItems()
      }
    } catch { /* ignore */ }
  }

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    return d.toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  }

  return (
    <>
      <TopBar title={t.pageTitle} />
      <div className="flex-1 flex flex-col overflow-hidden bg-brand-50">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-brand-900">{t.pageTitle}</h1>
              <p className="text-sm text-brand-500">{t.pageDesc}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {loading ? (
            <div className="text-center text-brand-400 py-20 text-sm">Loading...</div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Trash2 className="w-16 h-16 text-brand-200 mb-4" />
              <div className="text-lg font-semibold text-brand-500 mb-1">{t.empty}</div>
              <div className="text-sm text-brand-400">{t.emptyDesc}</div>
            </div>
          ) : (
            <div className="space-y-3 max-w-3xl">
              {items.map((item) => (
                <div
                  key={`${item.item_type}-${item.item_id}`}
                  className="bg-white rounded-xl border border-brand-200 p-4 hover:border-brand-300 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      item.item_type === 'matter' ? 'bg-blue-100' : 'bg-orange-100'
                    }`}>
                      {item.item_type === 'matter'
                        ? <FolderOpen className="w-5 h-5 text-blue-500" />
                        : <FileText className="w-5 h-5 text-orange-500" />
                      }
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          item.item_type === 'matter'
                            ? 'bg-blue-50 text-blue-600'
                            : 'bg-orange-50 text-orange-600'
                        }`}>
                          {item.item_type === 'matter' ? t.matter : t.file}
                        </span>
                        <span className="text-sm font-semibold text-brand-800 truncate">{item.title}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-brand-400">
                        {item.subtitle && (
                          <span>
                            {item.item_type === 'file' ? `${t.belongsTo}: ` : ''}
                            {item.subtitle}
                          </span>
                        )}
                        <span>{t.deletedAt}: {formatDate(item.deleted_at)}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleRestore(item)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-200 text-xs font-medium text-green-600 hover:bg-green-50 transition-colors"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        {t.restore}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(item)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-xs font-medium text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {t.deletePermanent}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Confirm permanent delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmDelete(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="absolute top-4 right-4 p-1 rounded text-brand-400 hover:text-brand-600"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-brand-800">{t.confirmTitle}</h3>
            </div>
            <p className="text-sm text-brand-600 mb-2">
              <span className="font-semibold">{confirmDelete.title}</span>
            </p>
            <p className="text-sm text-brand-500 mb-6">{t.confirmDesc}</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-lg border border-brand-200 text-sm text-brand-600 hover:bg-brand-50 transition-colors"
              >
                {t.cancel}
              </button>
              <button
                type="button"
                onClick={() => handlePermanentDelete(confirmDelete)}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
              >
                {t.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
